// Common component for Why section (contains image and content)
// Updated: horizontal carousel with prev/next arrows (HealthPrograms-style)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ImageCardGrid = ({ data, colsProp, cardClass, title, aspectRatioProp }) => {
  const navigate = useNavigate();

  // Derive stable values before hooks (safe — no conditional hook calls)
  const cols = colsProp ?? data?.cols ?? 4;
  const isSixCols = cols === 6;

  // ── Carousel state (mirrors HealthPrograms) ──────────────────────────────
  const [startIndex, setStartIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(cols);

  useEffect(() => {
    const updateCount = () => {
      const width = window.innerWidth;
      if (width >= 1024) setVisibleCount(cols);
      else if (width >= 640) setVisibleCount(Math.min(cols, 2));
      else setVisibleCount(1);
    };
    updateCount();
    window.addEventListener('resize', updateCount);
    return () => window.removeEventListener('resize', updateCount);
  }, [cols]);

  // Clamp startIndex when visibleCount changes
  useEffect(() => {
    const len = data?.items?.length ?? 0;
    setStartIndex((i) => Math.min(i, Math.max(0, len - visibleCount)));
  }, [visibleCount, data?.items?.length]);

  const itemsArr = data?.items ?? [];
  const hasMore = itemsArr.length > visibleCount;
  const maxStart = Math.max(0, itemsArr.length - visibleCount);
  const canPrev = startIndex > 0;
  const canNext = startIndex < maxStart;

  const visibleItems = itemsArr.slice(startIndex, startIndex + visibleCount);

  const handlePrev = () => { if (canPrev) setStartIndex((i) => i - 1); };
  const handleNext = () => { if (canNext) setStartIndex((i) => i + 1); };
  // ────────────────────────────────────────────────────────────────────────

  // Early return AFTER all hooks
  if (!data) return null;

  const {
    id,
    heading,
    heading2,
    bgColor = '#ffffff',
    cardBg = 'var(--ava-card-bg)',
    aspectRatio: dataAspectRatio = '16 / 9',
  } = data;

  const aspectRatio = aspectRatioProp || dataAspectRatio;

  return (
    <section
      id={id}
      className="w-full ava-section-gap"
      style={{ backgroundColor: bgColor }}
    >
      {title && (
        <p className="ava-heading-md text-center">{title}</p>
      )}

      <div className="container mx-auto px-4 md:px-8">
        {heading && (
          <h2 className="text-center ava-heading-lg ava-heading-gap">{heading}</h2>
        )}
        {heading2 && (
          <h2 className="text-center ava-text-subheading mb-3">{heading2}</h2>
        )}

        {/* ── Cards + Arrows ─────────────────────────────────────────────── */}
        <div className="relative">
          {/* Cards row */}
          <div
            className="grid gap-6"
            style={{
              gridTemplateColumns: `repeat(${visibleCount}, minmax(0, 1fr))`,
            }}
          >
            {visibleItems.map((item) => (
              <div
                key={item.id}
                className={`flex flex-col rounded-lg overflow-hidden ava-card-mukta ${cardClass ?? ''}`}
                style={{ backgroundColor: cardBg }}
              >
                {/* Image */}
                <div className="w-full flex-shrink-0 relative" style={{ aspectRatio }}>
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover object-center"
                    draggable={false}
                  />
                  {item.icon && (
                    <div
                      className="absolute left-4 bottom-[-15px] flex items-center justify-center rounded-full shadow-md ava-border border-white-500"
                      style={{
                        backgroundColor: item.iconbg,
                        width: '40px',
                        height: '40px',
                      }}
                    >
                      <img src={item.icon} alt="icon" className="w-4 h-4 object-contain" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="px-4 py-4 flex flex-col gap-2 flex-1">
                  <h3
                    className={`ava-text-subheading ${isSixCols ? 'text-xs md:text-sm' : 'text-sm md:text-base'
                      }`}
                  >
                    {item.title}
                  </h3>

                  {item.description && (
                    <p
                      className={`ava-text-body primary-text ${isSixCols ? 'text-[10px] md:text-xs' : 'text-xs md:text-sm'
                        }`}
                    >
                      {item.description}
                    </p>
                  )}

                  {item.commonConcerns && (
                    <>
                      <p style={{ textDecoration: 'underline' }} className="ava-text-subheading">
                        Common concerns
                      </p>
                      <ul className="space-y-1">
                        {item.commonConcerns.map((concern) => (
                          <li
                            key={concern.id}
                            className={`flex items-start gap-2 ${isSixCols ? 'text-[10px] md:text-xs' : 'ava-text-body'
                              }`}
                          >
                            <span>- {concern.item}</span>
                          </li>
                        ))}
                      </ul>
                      {item.href && (
                        <div className="mt-3">
                          <button
                            onClick={() => navigate(item.href)}
                            // className={`inline-flex items-center p-2 ava-border bg-[var(--ava-light-surface)] gap-1 transition-all rounded-md ${
                            //   isSixCols ? 'text-[10px] md:text-xs' : 'ava-text-body'
                            // }`}
                            className="ava-text-sm flex items-center transition-transform duration-300 group-hover:translate-x-1   gap-1 justify-center"
                            style={{color:"var(--primary-color)"}}

                          >
                            {item.ctaLabel || 'Explore'}
                            <svg
                              className="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              viewBox="0 0 24 24"
                            >
                              <path
                                d="M5 12h14M12 5l7 7-7 7"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Left Arrow ─────────────────────────────────────────────── */}
          {hasMore && (
            <button
              onClick={handlePrev}
              disabled={!canPrev}
              aria-label="Previous items"
              className="flex absolute -left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border transition-all duration-200"
              style={{
                borderColor: canPrev ? 'var(--primary-color)' : 'var(--gray-300)',
                opacity: canPrev ? 1 : 0.4,
                cursor: canPrev ? 'pointer' : 'not-allowed',
              }}
            >
              <img src="/images/left-arrow.svg" alt="Previous" className="w-10 h-10" />
            </button>
          )}

          {/* ── Right Arrow ─────────────────────────────────────────────── */}
          {hasMore && (
            <button
              onClick={handleNext}
              disabled={!canNext}
              aria-label="Next items"
              className="flex absolute -right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 items-center justify-center rounded-full bg-white border transition-all duration-200"
              style={{
                borderColor: canNext ? 'var(--primary-color)' : 'var(--gray-300)',
                opacity: canNext ? 1 : 0.4,
                cursor: canNext ? 'pointer' : 'not-allowed',
              }}
            >
              <img src="/images/right-arrow.svg" alt="Next" className="w-10 h-10" />
            </button>
          )}
        </div>
        {/* ────────────────────────────────────────────────────────────────── */}
      </div>
    </section>
  );
};

export default ImageCardGrid;